import { useEffect, useMemo, useRef, useState } from "react";
import type {
  DeepResearchProgressMode,
  DeepResearchProgressProps,
  DeepResearchResultProps,
  DeepSearchSource,
  ResearchStageId,
} from "../components/companion/DeepResearch";

/** Stable ref; `.read()` is reassigned each render so stale stream callbacks always see latest sources. */
export type DeepResearchHydrationReaderRef = {
  read: () => { sources: DeepSearchSource[]; durationSeconds: number };
};

type DeepResearchPhase = "progress" | "result" | null;

export type DeepResearchState = {
  phase: DeepResearchPhase;
  progress?: DeepResearchProgressProps;
  result?: DeepResearchResultProps;
} | null;

type DeepSearchStateEvent = {
  state: string;
  payload?: {
    elapsedSeconds?: number;
    activeTool?: string;
    sources?: Array<{ url?: string; title?: string }>;
  };
};

const STAGE_ORDER: ResearchStageId[] = ["understanding", "searching", "analyzing", "synthesizing", "finalizing"];

const buildRandomThresholds = () => {
  const searching = 5 + Math.floor(Math.random() * 11); // 5–15s (fast first step)
  const analyzing = searching + 30 + Math.floor(Math.random() * 31); // +30–60s
  const synthesizing = analyzing + 60 + Math.floor(Math.random() * 31); // +60–90s
  const finalizing = synthesizing + 60 + Math.floor(Math.random() * 31); // +60–90s (overall ≥ ~3.5min)
  return { searching, analyzing, synthesizing, finalizing };
};

const getDeepResearchTargetProgress = (currentStage: ResearchStageId, elapsedSeconds: number): number => {
  const idx = STAGE_ORDER.indexOf(currentStage);
  if (idx < 0) return 0;
  const base = (idx / STAGE_ORDER.length) * 90;
  const withinStage = Math.min(1, elapsedSeconds / 30) * (90 / STAGE_ORDER.length);
  return Math.min(95, base + withinStage);
};

export function useDeepResearch() {
  const [phase, setPhase] = useState<DeepResearchPhase>(null);
  const [progressMode, setProgressMode] = useState<DeepResearchProgressMode | null>(null);
  const [sourcesFound, setSourcesFound] = useState<DeepSearchSource[]>([]);
  const [currentStage, setCurrentStage] = useState<ResearchStageId>("understanding");
  const [displayProgress, setDisplayProgress] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [activeTool, setActiveTool] = useState<string | null>(null);

  const isDeepResearchStreamRef = useRef(false);
  /** Wall-clock start for the active phase (timer); null while preparing before the job is attached. */
  const streamStartRef = useRef<number | null>(null);
  /** Kept in sync whenever sources are merged (stream events); onComplete must not rely on React state closures. */
  const sourcesFoundRef = useRef<DeepSearchSource[]>([]);
  const deepResearchHydrationReaderRef = useRef<DeepResearchHydrationReaderRef>({
    read: () => ({ sources: [], durationSeconds: 0 }),
  });
  const thresholdsRef = useRef<{
    searching: number;
    analyzing: number;
    synthesizing: number;
    finalizing: number;
  } | null>(null);

  const start = () => {
    thresholdsRef.current = buildRandomThresholds();
    setPhase("progress");
    setProgressMode("preparing");
    setSourcesFound([]);
    sourcesFoundRef.current = [];
    setCurrentStage("understanding");
    setDisplayProgress(0);
    setElapsedSeconds(0);
    setDurationSeconds(0);
    setActiveTool(null);
    streamStartRef.current = null;
    isDeepResearchStreamRef.current = true;
  };

  /** Call when the deep_research tool starts and a background job id exists — begins the per-second timer. */
  const activateDeepResearchProgress = (startedAtMs?: number) => {
    setProgressMode("active");
    const nextStartMs = startedAtMs ?? Date.now();
    streamStartRef.current = nextStartMs;
    setElapsedSeconds(Math.max(0, Math.floor((Date.now() - nextStartMs) / 1000)));
  };

  const reset = () => {
    setPhase(null);
    setProgressMode(null);
    setSourcesFound([]);
    sourcesFoundRef.current = [];
    setCurrentStage("understanding");
    setDisplayProgress(0);
    setElapsedSeconds(0);
    setDurationSeconds(0);
    setActiveTool(null);
    thresholdsRef.current = null;
    streamStartRef.current = null;
    isDeepResearchStreamRef.current = false;
  };

  useEffect(() => {
    if (phase !== "progress" || progressMode !== "active" || streamStartRef.current == null) {
      return;
    }
    const startMs = streamStartRef.current;
    const tick = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startMs) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [phase, progressMode]);

  const handleStreamComplete = () => {
    const wasDeepResearchStream = isDeepResearchStreamRef.current;
    if (wasDeepResearchStream && streamStartRef.current != null) {
      const secs = Math.round((Date.now() - streamStartRef.current) / 1000);
      setDurationSeconds(secs);
    }
    if (wasDeepResearchStream) {
      setDisplayProgress(100);
      setPhase("result");
      setProgressMode(null);
      setCurrentStage("finalizing");
    }
    isDeepResearchStreamRef.current = false;
  };

  const getElapsedSecondsForLogic = () =>
    streamStartRef.current != null ? Math.max(0, Math.floor((Date.now() - streamStartRef.current) / 1000)) : 0;

  const handleStateEvent = ({ state, payload }: DeepSearchStateEvent) => {
    const thresholds =
      thresholdsRef.current ??
      ({
        searching: 5,
        analyzing: 20,
        synthesizing: 60,
        finalizing: 120,
      } as const);

    if (state === "understanding") {
      setPhase("progress");
      setCurrentStage("understanding");
      setActiveTool(null);
      setDisplayProgress((prev) => Math.max(prev, 5));
    } else if (state === "searching") {
      setPhase("progress");
      setCurrentStage("searching");
      setDisplayProgress((prev) => Math.max(prev, 25));
    } else if (state === "analyzing") {
      setPhase("progress");
      setCurrentStage("analyzing");
      setDisplayProgress((prev) => Math.max(prev, 50));
    } else if (state === "synthesizing") {
      setPhase("progress");
      setCurrentStage("synthesizing");
      setDisplayProgress((prev) => Math.max(prev, 75));
    } else if (state === "finalizing") {
      setPhase("progress");
      setCurrentStage("finalizing");
      setDisplayProgress((prev) => Math.max(prev, 90));
    } else if (state === "using_code_interpreter") {
      setPhase("progress");
      setActiveTool(typeof payload?.activeTool === "string" ? payload.activeTool : "code_interpreter");
    } else if (state === "heartbeat") {
      setPhase("progress");
      const elapsed = getElapsedSecondsForLogic();
      if (elapsed >= thresholds.finalizing) setCurrentStage("finalizing");
      else if (elapsed >= thresholds.synthesizing) setCurrentStage("synthesizing");
      else if (elapsed >= thresholds.analyzing) setCurrentStage("analyzing");
      else if (elapsed >= thresholds.searching) setCurrentStage("searching");
      else setCurrentStage("understanding");
      setDisplayProgress((prev) =>
        Math.max(
          prev,
          getDeepResearchTargetProgress(
            elapsed >= thresholds.finalizing
              ? "finalizing"
              : elapsed >= thresholds.synthesizing
                ? "synthesizing"
                : elapsed >= thresholds.analyzing
                  ? "analyzing"
                  : elapsed >= thresholds.searching
                    ? "searching"
                    : "understanding",
            elapsed
          )
        )
      );
    } else if (state === "completed") {
      setActiveTool(null);
    } else if (state === "error") {
      reset();
    } else if (state === "sources" && Array.isArray(payload?.sources)) {
      const raw = payload.sources as Array<{ id?: string; url?: string; title?: string }>;
      const next: DeepSearchSource[] = raw.map((s, i) => ({
        id: typeof s.id === "string" && s.id.length > 0 ? s.id : `${s.url ?? ""}-${i}`,
        url: s.url ?? "",
        title: s.title,
        type: "web",
      }));
      setSourcesFound((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        const added = next.filter((x) => !seen.has(x.id));
        const merged = added.length ? [...prev, ...added] : prev;
        sourcesFoundRef.current = merged;
        return merged;
      });
    }
  };

  deepResearchHydrationReaderRef.current.read = () => ({
    sources: sourcesFoundRef.current.slice(),
    durationSeconds: streamStartRef.current != null ? Math.round((Date.now() - streamStartRef.current) / 1000) : 0,
  });

  const deepResearchState: DeepResearchState = useMemo(
    () =>
      phase != null
        ? {
            phase,
            ...(phase === "progress" &&
              progressMode != null && {
                progress: {
                  currentStage,
                  progress: displayProgress,
                  sourcesFound,
                  elapsedSeconds: progressMode === "active" ? elapsedSeconds : undefined,
                  progressMode,
                  sourcesCount: sourcesFound.length,
                  activeTool: activeTool ?? undefined,
                },
              }),
            ...(phase === "result" && {
              result: {
                sources: sourcesFound,
                durationSeconds,
              },
            }),
          }
        : null,
    [phase, progressMode, currentStage, displayProgress, sourcesFound, elapsedSeconds, activeTool, durationSeconds]
  );

  return {
    deepResearchState,
    deepSearchPhase: phase,
    deepSearchCurrentStage: currentStage,
    deepSearchElapsedSeconds: elapsedSeconds,
    deepSearchDisplayProgress: displayProgress,
    deepSearchDurationSeconds: durationSeconds,
    deepSearchActiveTool: activeTool,
    deepSearchSourcesFound: sourcesFound,
    startDeepResearch: start,
    activateDeepResearchProgress,
    resetDeepResearch: reset,
    handleDeepResearchStateEvent: handleStateEvent,
    handleDeepResearchStreamComplete: handleStreamComplete,
    isDeepResearchStreamRef,
    deepSearchStreamStartRef: streamStartRef,
    deepResearchHydrationReaderRef,
  };
}
